import { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Link,
  Input,
} from '@heroui/react';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { useAuthStore, validateAuthState } from '@/stores/auth.store';
import TermsModal from './TermsModal';
import PrivacyPolicyModal from './PrivacyPolicyModal';
import SignUpConfirmModal from './SignUpConfirmModal';

import canvasLogin from '@/assets/canvasLogin.webp';
import logoMacroindustrial from '@/assets/logo-macroindustrial.png';
import googleLogo from '@/assets/google.webp';
import { registerUsuario } from '@/services/empresa/users.service';
import { generateStateKey } from '@/utils/stateKeyGenerator';

interface SignUpModalProps {
  enableGoogle?: boolean;
  enableMicrosoft?: boolean;
  enableFacebook?: boolean;
  onGoogleLogin?: () => void;
  onMicrosoftLogin?: () => void;
  onFacebookLogin?: () => void;
  className?: string;
  showUsername?: boolean;
  isLoggedInProp?: boolean;
  isOpen?: boolean;
  onClose?: () => void;
  hideTriggerButton?: boolean;
  onSwitchToLogin?: () => void; // Add prop to switch to login modal
}


// Component that handles the signup button and modal
const SignUpModal = ({
  enableGoogle = true,
  enableMicrosoft = true,
  enableFacebook = true,
  onGoogleLogin,
  onMicrosoftLogin,
  onFacebookLogin,
  className = "",
  showUsername = true,
  isOpen: isOpenProp,
  onClose: onCloseProp,
  hideTriggerButton = false,
  onSwitchToLogin,
}: SignUpModalProps) => {
  const [checkingAuth, setCheckingAuth] = useState<boolean>(true);
  const [internalModalOpen, setInternalModalOpen] = useState<boolean>(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState<boolean>(false);
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState<boolean>(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
  const { user, isLoggedIn, clearCredentials, logout } = useAuthStore();

  const isModalOpen = isOpenProp !== undefined ? isOpenProp : internalModalOpen;

  const handleOpenModal = () => {
    setInternalModalOpen(true);
  };

  const handleCloseModal = () => {
    if (onCloseProp) {
      onCloseProp();
    } else {
      setInternalModalOpen(false);
    }
  };

  const togglePasswordVisibility = () => setIsPasswordVisible(!isPasswordVisible);
  const toggleConfirmPasswordVisibility = () => setIsConfirmPasswordVisible(!isConfirmPasswordVisible);

  // Check authentication status on component mount
  useEffect(() => {
    checkAuthStatus();
    return () => {
      setCheckingAuth(false);
    };
  }, []);

  const checkAuthStatus = useCallback(() => {
    // Use the auth store to check authentication status
    const isValid = validateAuthState();
    if (!isValid) {
      clearCredentials();
    }
    setCheckingAuth(false);
  }, []);

  const handleGoogleLogin = () => {
    if (onGoogleLogin) {
      onGoogleLogin();
    } else {
      // Default behavior: redirect to Google auth endpoint
      // Close modal before redirecting
      handleCloseModal();
      generateStateKey();
      window.location.href = `${import.meta.env.VITE_API_URL}/connect/google`;
    }
  };

  const handleMicrosoftLogin = () => {
    if (onMicrosoftLogin) {
      onMicrosoftLogin();
    } else {
      // Default behavior: redirect to Microsoft auth endpoint
      // Close modal before redirecting
      handleCloseModal();
      window.location.href = `${import.meta.env.VITE_API_URL}/connect/microsoft`;
    }
  };

  const handleFacebookLogin = () => {
    if (onFacebookLogin) {
      onFacebookLogin();
    } else {
      // Default behavior: redirect to Facebook auth endpoint
      // Close modal before redirecting
      // You might need to implement this based on your backend setup
      handleCloseModal();
      window.location.href = `${import.meta.env.VITE_API_URL}/connect/facebook`;
    }
  };

  const handleSignUpWithEmailPassword = async () => {
    // Validate that passwords match
    if (password !== confirmPassword) {
      alert('Las contraseñas no coinciden. Por favor inténtalo de nuevo.');
      return;
    }
    
    // Validate that terms and conditions are accepted
    if (!termsAccepted) {
      alert('Debes aceptar los Términos y Condiciones para registrarte.');
      return;
    }
    
    try {      
      await registerUsuario(email, password );
      
      // Close the modal after successful registration
      handleCloseModal();
      
      // Show the confirmation modal
      setShowConfirmModal(true);
    } catch (error) {
      console.error('Sign up error:', error);
      alert('Error en el registro. Por favor intenta de nuevo.');
    }
  };

  const handleLogout = async () => {
    // Call the logout function which makes API call to Strapi and clears credentials
    await logout();
    // Close the modal after logout
    handleCloseModal();
  };

  // Check authentication status when modal is opened
  useEffect(() => {
    if (isModalOpen) {
      checkAuthStatus();
    }
  }, [isModalOpen, checkAuthStatus]);

  // Function to render the login button that appears in the navbar
  const renderNavButton = () => {
    if (checkingAuth) {
      return (
        <button className={`ml-4 px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 ${className}`}>
          Verificando...
        </button>
      );
    }

    if (isLoggedIn) {
      return (
        <div className="flex items-center gap-2">
          {showUsername && user?.username && <span className="text-sm"> {user.username}</span>}
          <button 
            onClick={handleLogout}
            className={`px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 ${className}`}
          >
            Cerrar Sesión
          </button>
        </div>
      );
    } else {
      return (
        <button 
          onClick={handleOpenModal}
          className={`ml-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 ${className}`}
        >
          Iniciar Sesión
        </button>
      );
    }
  };

  if (checkingAuth) {
    return (
      <>
        {!hideTriggerButton && renderNavButton()}
      </>
    );
  }

  return (
    <>
      {!hideTriggerButton && renderNavButton()}
      <Modal 
          isOpen={isModalOpen} 
          onClose={handleCloseModal}
          placement="center"
          backdrop="blur"
          size="5xl"
        >
          <ModalContent className="max-w-6xl h-auto p-0 max-h-[90vh] overflow-auto">
            {/* Responsive grid: single column on mobile, two columns on desktop */}
            <div className="grid grid-cols-1 lg:grid-cols-2 h-full">
              {/* Left side - Image for larger screens (hidden on mobile) */}
              <div 
                className="hidden lg:flex flex-col items-center justify-center p-12 relative" 
                style={{ 
                  backgroundImage: `url(${canvasLogin})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat"
                }}
              >

                <div className="absolute inset-0 bg-white/30"></div>
                <div className="max-w-md w-full text-center relative z-10">
                  <div className="absolute top-4 left-4 z-20">
                    <img 
                      src={logoMacroindustrial} 
                      alt="Macroindustrial Logo" 
                      className="w-96 object-contain rounded-md shadow-md" 
                    />
                  </div>
                </div>
              </div>
              
              {/* Right side - Login form (visible on all screens) */}
              <div className="flex flex-col">
                <ModalHeader className="flex flex-col gap-1 p-6">
                  <div className="flex md:hidden flex-col gap-4 mb-4">
                    <p className="text-[24px] leading-[140%] max-sm:text-[20px] text-center font-bold">
                      Regístrate
                    </p>
                    <p className="text-[12px] leading-[150%] text-heading text-center w-8/12 mx-auto max-sm:w-full">
                      Únete para empezar a gestionar tu negocio eficientemente.
                    </p>
                  </div>
                  <div className="hidden md:flex flex-col gap-4 mb-6">
                    <p className="text-[24px] leading-[140%] max-sm:text-[20px] text-center font-bold">
                      Regístrate
                    </p>
                    <p className="text-[12px] leading-[150%] text-heading text-center w-8/12 mx-auto max-sm:w-full">
                      Únete para empezar a gestionar tu negocio eficientemente.
                    </p>
                  </div>
                </ModalHeader>
                
                <ModalBody className="space-y-4 py-4 px-6 flex-grow">
                  
                  {isLoggedIn ? (
                    // Show logout option if user is logged in
                    <div className="flex flex-col items-center py-6">
                      <p className="text-center mb-4">
                        ¡Sesión iniciada correctamente!
                      </p>
                      <Button 
                        color="danger" 
                        variant="flat"
                        onPress={handleLogout}
                      >
                        Cerrar Sesión
                      </Button>
                    </div>
                  ) : (
                    // Show login options if user is not logged in
                    <>
                      {/* Email/password login form */}
                      <div className="flex flex-col space-y-4">                        
                        <Input                          
                          label="Correo Electrónico"
                          type="email"
                          variant="bordered"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          isRequired
                        />
                        <Input
                          label="Contraseña"
                          variant="bordered"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          isRequired
                          endContent={
                            <button className="focus:outline-none" type="button" onClick={togglePasswordVisibility}>
                              {isPasswordVisible ? (
                                <EyeSlashIcon className="w-5 h-5 text-gray-500" />
                              ) : (
                                <EyeIcon className="w-5 h-5 text-gray-500" />
                              )}
                            </button>
                          }
                          type={isPasswordVisible ? "text" : "password"}
                        />
                        <Input
                          label="Confirmar Contraseña"
                          variant="bordered"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          isRequired
                          endContent={
                            <button className="focus:outline-none" type="button" onClick={toggleConfirmPasswordVisibility}>
                              {isConfirmPasswordVisible ? (
                                <EyeSlashIcon className="w-5 h-5 text-gray-500" />
                              ) : (
                                <EyeIcon className="w-5 h-5 text-gray-500" />
                              )}
                            </button>
                          }
                          type={isConfirmPasswordVisible ? "text" : "password"}
                        />
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            id="terms"
                            checked={termsAccepted}
                            onChange={(e) => setTermsAccepted(e.target.checked)}
                            className="mt-1 w-4 h-4 text-orange-600 bg-gray-100 border-gray-300 rounded focus:ring-orange-500 focus:ring-2"
                          />
                          <label htmlFor="terms" className="text-sm text-gray-700 dark:text-gray-300">
                            Acepto los{' '}
                            <button
                              type="button"
                              className="text-orange-600 hover:underline"
                              onClick={(e) => {
                                e.preventDefault();
                                setIsTermsModalOpen(true);
                              }}
                            >
                              Términos y Condiciones
                            </button>
                            {' '}y la{' '}
                            <button
                              type="button"
                              className="text-orange-600 hover:underline"
                              onClick={(e) => {
                                e.preventDefault();
                                setIsPrivacyModalOpen(true);
                              }}
                            >
                              Política de Privacidad
                            </button>
                          </label>
                        </div>
                        <Button 
                          variant="solid" 
                          className="w-full"
                          onPress={handleSignUpWithEmailPassword}
                          isDisabled={!email || !password || !confirmPassword || !termsAccepted}
                        >
                          Crear Cuenta
                        </Button>
                      </div>
                      
                      {/* Or separator */}
                      <div className="flex items-center my-4">
                        <div className="flex-grow border-t border-gray-300"></div>
                        <span className="mx-4 text-gray-500 text-sm">o</span>
                        <div className="flex-grow border-t border-gray-300"></div>
                      </div>
                      
                      {/* Social login options */}
                      {(enableGoogle || enableMicrosoft || enableFacebook) && (
                        <div className="flex flex-col gap-2">
                          {enableGoogle && (
                            <Button 
                              variant="bordered" 
                              className="w-full"
                              startContent={
                                <img src={googleLogo} alt="Google logo" className="w-5 h-5 object-contain" />
                              }
                              onPress={handleGoogleLogin}
                            >
                              Google
                              
                            </Button>
                          )}
                          
                          {enableMicrosoft && (
                            <Button 
                              variant="bordered" 
                              className="w-full"
                              startContent={
                                <svg className="w-5 h-5" viewBox="0 0 24 24">
                                  <path 
                                    fill="currentColor" 
                                    d="M11.4 24H0V12.6h11.4V24zM24 0H12.6v11.4H24V0zm-12.6 0H0v11.4h11.4V0zM24 24H12.6V12.6H24V24z"
                                  />
                                </svg>
                              }
                              onPress={handleMicrosoftLogin}
                            >
                              Microsoft
                            </Button>
                          )}
                          
                          {enableFacebook && (
                            <Button 
                              variant="bordered" 
                              className="w-full"
                              startContent={
                                <svg className="w-5 h-5" viewBox="0 0 24 24">
                                  <path 
                                    fill="currentColor" 
                                    d="M22.675 0H1.325C.593 0 0 .593 0 1.325v21.351C0 23.407.593 24 1.325 24H12.82v-9.294H9.692v-3.622h3.128V8.413c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.795.143v3.24l-1.918.001c-1.504 0-1.795.715-1.795 1.763v2.313h3.587l-.467 3.622h-3.12V24h6.116c.73 0 1.323-.593 1.323-1.325V1.325C24 .593 23.407 0 22.675 0z"
                                  />
                                </svg>
                              }
                              onPress={handleFacebookLogin}
                            >
                              Facebook
                            </Button>
                          )}
                        </div>
                      )}
                      <p className="text-sm text-center mt-4">
                        ¿Ya tienes una cuenta?{' '}
                        <Link 
                          className="text-link underline cursor-pointer"
                          onPress={onSwitchToLogin}
                        >
                          Iniciar Sesión
                        </Link>
                      </p>
                    </>
                  )}
                </ModalBody>
                
                <ModalFooter className="px-6 pb-6">
                </ModalFooter>
              </div>
            </div>
          </ModalContent>
        </Modal>
        
        {/* Terms Modal */}
        <TermsModal 
          isOpen={isTermsModalOpen} 
          onClose={() => setIsTermsModalOpen(false)} 
        />
        
        {/* Privacy Policy Modal */}
        <PrivacyPolicyModal 
          isOpen={isPrivacyModalOpen} 
          onClose={() => setIsPrivacyModalOpen(false)} 
        />
        
        {/* Sign Up Confirmation Modal */}
        <SignUpConfirmModal
          isOpen={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          email={email}
        />
    </>
  );
};

export default SignUpModal;